import { defineComponent } from 'vue'

import RemediationPatchMutation from '~/apollo/mutations/dependency/createRemediationPatch.gql'
import TriggerRemediationRunMutation from '~/apollo/mutations/dependency/triggerRemediationRun.gql'
import FixPathsQuery from '~/apollo/queries/dependency/fixPaths.gql'
import OccurrenceQuery from '~/apollo/queries/dependency/occurrence.gql'
import RepositoryTargetQuery from '~/apollo/queries/dependency/repositoryTarget.gql'
import RepositoryTargetCountsQuery from '~/apollo/queries/dependency/repositoryTargetCounts.gql'

import { GraphqlMutationResponse, GraphqlQueryResponse } from '~/types/apolloTypes'
import {
  FetchRepositoryTargetQuery,
  FetchTargetCountsQuery,
  PackageType,
  RepositoryTarget,
  VulnerabilityOccurrence,
  VulnerabilityOccurrenceFixability,
  VulnerabilityRemediationFixPath
} from '~/types/types'
import { resolveNodes } from '~/utils/array'
import { generateGlobalId } from '~/utils/hashId'
import routeQueryMixin from './routeQueryMixin'

export class DependencyError extends Error {
  httpError: number | null
  constructor(message: string, cause: Error | null = null, httpError: number | null = null) {
    super(message)
    this.name = 'DependencyError'
    this.cause = cause
    this.httpError = httpError
    Object.setPrototypeOf(this, DependencyError.prototype)
  }
}

export const VULN_PAGE_NAME = 'provider-owner-repo-dependencies-target-vulnerabilities'
export const PACK_PAGE_NAME = 'provider-owner-repo-dependencies-target-packages'

export default defineComponent({
  name: 'DependencyMixin',
  mixins: [routeQueryMixin],
  data() {
    return {
      PackageType,
      repositoryTarget: {} as RepositoryTarget,
      counts: {
        packages: {
          [PackageType.Direct]: 0,
          [PackageType.Transitive]: 0
        },
        vulnerabilities: {
          [PackageType.Direct]: 0,
          [PackageType.Transitive]: 0
        } as Record<PackageType, number>
      }
    }
  },
  methods: {
    async fetchTargetMetadata(fromCache = false) {
      try {
        const fetchFunc = fromCache ? this.$fetchGraphqlDataFromCache : this.$fetchGraphqlData
        const response = await fetchFunc(RepositoryTargetQuery, {
          id: this.routeTargetId
        })

        if (response === null) {
          return
        }

        const dataObj: FetchRepositoryTargetQuery = fromCache ? response : response.data
        //@ts-expect-error - We don't fetch all the fields in the RepositoryTarget type
        this.repositoryTarget = dataObj.repositoryTarget
      } catch (e) {
        if (fromCache) {
          return
        }

        const FourOFourMsgs = [
          'Repository target does not exist',
          'You do not have permission to perform this action'
        ]
        if (
          FourOFourMsgs.some((msg) =>
            (e as Error).message.toLowerCase().includes(msg.toLowerCase())
          )
        ) {
          this.$nuxt.error({ statusCode: 404, message: 'Dependency target not found.' })
          return
        }

        this.$logErrorAndToast(e as Error, 'Failed to fetch target metadata.')
      }
    },
    async fetchCounts(filters: Record<string, string[] | null>, fromCache = false) {
      const { owner, provider, repo } = this.$route.params

      try {
        const fetchFunc = fromCache ? this.$fetchGraphqlDataFromCache : this.$fetchGraphqlData
        const response: GraphqlQueryResponse = await fetchFunc(RepositoryTargetCountsQuery, {
          name: repo,
          owner,
          targetId: this.routeTargetId,
          provider: this.$providerMetaMap[provider].value,
          ...filters
        })

        if (response === null) {
          return
        }

        //@ts-expect-error - We don't fetch all the fields in the Repository type
        const data: FetchTargetCountsQuery = fromCache ? response : response.data

        this.counts = {
          packages: {
            [PackageType.Direct]: data.repository?.directPackages.totalCount || 0,
            [PackageType.Transitive]: data.repository?.transitivePackages.totalCount || 0
          },
          vulnerabilities: {
            [PackageType.Direct]: data.repository?.directVulnerabilities.totalCount || 0,
            [PackageType.Transitive]: data.repository?.transitiveVulnerabilities.totalCount || 0,
            [PackageType.Unknown]: 0
          }
        }
      } catch (e) {
        this.$logErrorAndToast(
          e as Error,
          fromCache ? undefined : 'Failed to fetch counts of vulnerabilities.'
        )
      }
    },
    async doRemediationPatch(fixPathId: VulnerabilityRemediationFixPath['id']) {
      try {
        const response: GraphqlMutationResponse = await this.$applyGraphqlMutation(
          RemediationPatchMutation,
          {
            fixPathId
          }
        )

        return response.data?.createRemediationPatchForVulnerabilityOccurrence
      } catch (e) {
        this.$logErrorAndToast(e as Error, 'Failed to create remediation patch.')
      }
      return undefined
    },

    async doFixRun(occurrence: VulnerabilityOccurrence | null) {
      if (
        ![
          VulnerabilityOccurrenceFixability.PossiblyFixable,
          VulnerabilityOccurrenceFixability.Error
        ].includes(occurrence?.fixability as VulnerabilityOccurrenceFixability)
      ) {
        return
      }
      try {
        await this.$applyGraphqlMutation(TriggerRemediationRunMutation, {
          occurrenceId: occurrence?.id
        })
      } catch (e) {
        this.$logErrorAndToast(e as Error, 'Failed to trigger fix run.')
      }
    },

    async fetchOccurrence(
      occurrenceId: string,
      refetch = false,
      fromCache = false
    ): Promise<VulnerabilityOccurrence | null> {
      const { owner, provider, repo } = this.$route.params

      const occurrenceIdArg = occurrenceId || this.$route.params.occurrence
      if (!occurrenceIdArg) {
        return null
      }

      const fetchFunc = fromCache ? this.$fetchGraphqlDataFromCache : this.$fetchGraphqlData

      try {
        const response: GraphqlQueryResponse = await fetchFunc(
          OccurrenceQuery,
          {
            owner,
            provider: this.$providerMetaMap[provider].value,
            name: repo,
            target: this.routeTargetId,
            occurrenceId: occurrenceId ? occurrenceId : this.routeOccurrenceId
          },
          refetch
        )

        return response?.data?.repository?.vulnerabilityOccurrence ?? null
      } catch (e) {
        const FourOFourMsgs = [
          'Occurrence does not exist',
          'You do not have permission to perform this action'
        ]
        if (
          FourOFourMsgs.some((msg) =>
            (e as Error).message.toLowerCase().includes(msg.toLowerCase())
          )
        ) {
          throw new DependencyError('Vulnerability occurrence not found.', e as Error, 404)
        }
        throw new DependencyError('Failed to fetch occurrence.', e as Error)
      }
    },
    async fetchFixPaths(occurrenceId: VulnerabilityOccurrence['id'], refetch = false) {
      try {
        const { owner, provider, repo } = this.$route.params
        const response: GraphqlQueryResponse = await this.$fetchGraphqlData(
          FixPathsQuery,
          {
            owner,
            name: repo,
            provider: this.$providerMetaMap[provider].value,
            occurrenceId: occurrenceId || this.routeOccurrenceId
          },
          refetch
        )
        return resolveNodes(response.data.repository?.vulnerabilityOccurrence?.fixPaths)
      } catch (e) {
        this.$logErrorAndToast(
          e as Error,
          'We encountered an error while fetching fix paths for this vulnerability. Please try again later or contact support.'
        )
        return []
      }
    }
  },

  computed: {
    routeTargetId() {
      const { target } = this.$route.params
      return generateGlobalId('RepositoryTarget', target)
    },
    routeOccurrenceId() {
      const { occurrence } = this.$route.params
      return generateGlobalId('VulnerabilityOccurrence', occurrence)
    },
    activeCategory() {
      const routeName = this.$route.name || ''
      if (routeName.includes(VULN_PAGE_NAME)) return 'vulnerabilities'
      if (routeName.includes(PACK_PAGE_NAME)) return 'packages'
      return ''
    },
    activeSubCategory(): PackageType | '' {
      return (this.$route.query.pkgType as PackageType) || ''
    },
    showDirect() {
      return this.queryParams.pkgType === PackageType.Direct || !this.queryParams.pkgType
    },
    showTransitive() {
      return this.queryParams.pkgType === PackageType.Transitive || !this.queryParams.pkgType
    },
    targetTitle() {
      return this.repositoryTarget?.manifestPath || this.repositoryTarget?.lockfilePath
    },
    pkgType() {
      return this.queryParams.pkgType
    }
  }
})
